<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import { DialogCloseButton, DialogDrawer } from '@ilteducation/components';
import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import useLocale from '../../../composables/use-locale';
import { useVideoQuestions } from '../../composables/useVideoQuestions';

const props = defineProps<{
  modelValue: boolean;
  video: { resourceId: string; title: string; thumbnail?: string; selectedQuestions?: CloseEndedQuestion[] };
  loading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
  (e: 'save', questions: CloseEndedQuestion[]): void;
}>();

const { t } = useLocale();

const {
  questions,
  loading: questionsLoading,
  error,
  fetch,
} = useVideoQuestions(computed(() => props.video.resourceId));

const selectedQuestionIds = ref(new Set<string>());
const selectionInitialized = ref(false);

watch(
  () => props.modelValue,
  (newVal) => {
    if (newVal === true) {
      selectedQuestionIds.value = new Set();
      selectionInitialized.value = false;
      fetch();
    }
  },
  { immediate: true },
);

watch(questions, (qs) => {
  if (selectionInitialized.value || qs.length === 0) {
    return;
  }
  const prior = props.video.selectedQuestions;
  if (prior && prior.length > 0) {
    selectedQuestionIds.value = new Set(prior.map((q) => q.id!).filter(Boolean));
  } else {
    selectedQuestionIds.value = new Set(qs.map((q) => q.id!).filter(Boolean));
  }
  selectionInitialized.value = true;
});

const toggleQuestion = (id: string, checked: boolean) => {
  const next = new Set(selectedQuestionIds.value);
  if (checked) {
    next.add(id);
  } else {
    next.delete(id);
  }
  selectedQuestionIds.value = next;
};

const handleClose = () => {
  if (props.loading) {
    return;
  }
  emit('update:modelValue', false);
};

const handleSave = () => {
  if (props.loading) {
    return;
  }
  const selected = questions.value.filter((q) => selectedQuestionIds.value.has(q.id!));
  emit('save', selected);
  emit('update:modelValue', false);
};
</script>

<template>
  <DialogDrawer :model-value="modelValue" :titleText="video.title" maxWidth="500" @close="handleClose">
    <template #dialogTopbar>
      <DialogCloseButton @click="handleClose" icon="fa-whiteboard fa-times" />
      <v-card-title class="pb-6 text-center position-relative">
        <h2 class="chalkduster-font ma-0 text-wrap px-14">{{ video.title }}</h2>
      </v-card-title>
    </template>

    <div v-if="questionsLoading" class="d-flex justify-center pa-4">
      <v-progress-circular indeterminate></v-progress-circular>
    </div>
    <div v-else-if="error" class="text-error pa-2">
      {{ t('error_loading_questions') }}
    </div>
    <v-list v-else class="questions-list overflow-y-auto" max-height="300">
      <v-list-item v-for="question in questions" :key="question.id ?? question.phrase">
        <v-checkbox
          :model-value="selectedQuestionIds.has(question.id!)"
          :label="question.phrase"
          hide-details
          @update:model-value="(checked) => toggleQuestion(question.id!, Boolean(checked))"
        ></v-checkbox>
      </v-list-item>
    </v-list>

    <template #actions>
      <v-btn size="large" variant="outlined" rounded="default" @click="handleClose">
        <template #append>
          <v-icon icon="fa:fawsb fa-times" size="small"></v-icon>
        </template>
        {{ t('button_cancel') }}
      </v-btn>
      <v-btn
        size="large"
        variant="flat"
        :loading="loading"
        :disabled="questionsLoading || selectedQuestionIds.size === 0"
        @click="handleSave"
      >
        {{ t('button_save') }}
      </v-btn>
    </template>
  </DialogDrawer>
</template>

<style scoped lang="scss">
.questions-list {
  background-color: transparent;

  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(var(--v-theme-on-surface), 0.3);
    border-radius: 8px;
    border: 2px solid transparent;
    background-clip: content-box;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(var(--v-theme-on-surface), 0.5);
  }
}
</style>
